import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const STEPS = ["Votre projet", "Votre ouvrage", "Vos coordonnées", "Récapitulatif"];

const typesTravaux = [
  { value: "toiture_terrasse", label: "Toiture-terrasse", icon: "ri-building-2-line" },
  { value: "parking", label: "Parking (enrobés & asphalte)", icon: "ri-road-map-line" },
  { value: "dalle_carrossable", label: "Dalle carrossable", icon: "ri-truck-line" },
  { value: "terrasse", label: "Terrasse", icon: "ri-home-smile-line" },
  { value: "etancheite_isolation", label: "Étanchéité + isolation thermique", icon: "ri-temp-hot-line" },
  { value: "refection", label: "Réfection étanchéité existante", icon: "ri-refresh-line" },
  { value: "autre", label: "Autre", icon: "ri-more-line" },
];

const surfaces = [
  "Moins de 100 m²",
  "100 – 500 m²",
  "500 – 1 000 m²",
  "1 000 – 5 000 m²",
  "Plus de 5 000 m²",
  "Je ne sais pas encore",
];

const delais = [
  "Urgent (moins d'1 mois)",
  "Court terme (1 – 3 mois)",
  "Moyen terme (3 – 6 mois)",
  "Long terme (+ de 6 mois)",
  "Pas encore défini",
];

const typesBatiment = [
  "Bâtiment tertiaire (bureaux, commerces)",
  "Bâtiment industriel / entrepôt",
  "Bâtiment résidentiel collectif",
  "Ouvrage d'art / infrastructure",
  "Établissement recevant du public (ERP)",
  "Autre",
];

interface FormData {
  type_travaux: string;
  description_projet: string;
  surface: string;
  delai: string;
  type_batiment: string;
  localisation: string;
  support_existant: string;
  contraintes: string;
  prenom: string;
  nom: string;
  email: string;
  telephone: string;
  societe: string;
  poste: string;
}

const initialData: FormData = {
  type_travaux: "",
  description_projet: "",
  surface: "",
  delai: "",
  type_batiment: "",
  localisation: "",
  support_existant: "",
  contraintes: "",
  prenom: "",
  nom: "",
  email: "",
  telephone: "",
  societe: "",
  poste: "",
};

export default function DevisPage() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormData>(initialData);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [descCount, setDescCount] = useState(0);
  const [contraintesCount, setContraintesCount] = useState(0);

  const set = (key: keyof FormData, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const canNext = () => {
    if (step === 0) return !!form.type_travaux;
    if (step === 1) return !!form.surface && !!form.localisation;
    if (step === 2) return !!form.prenom && !!form.nom && !!form.email && !!form.telephone;
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    const body = new URLSearchParams();
    (Object.keys(form) as (keyof FormData)[]).forEach((k) => body.append(k, form[k]));

    try {
      const res = await fetch("https://readdy.ai/api/form/d7j2gcg3a2kq2q6kqneg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) setSubmitted(true);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const selectedType = typesTravaux.find((t) => t.value === form.type_travaux);

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20 bg-white flex items-center justify-center px-4">
        <div className="max-w-lg w-full text-center py-20">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-[#00a340] text-white mx-auto mb-6">
            <i className="ri-checkbox-circle-line text-4xl"></i>
          </div>
          <h1
            className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Demande envoyée !
          </h1>
          <p className="text-gray-500 text-sm leading-relaxed mb-8">
            Merci <strong className="text-gray-800">{form.prenom} {form.nom}</strong>. Votre demande de devis a bien été reçue. Notre équipe vous recontactera sous <strong className="text-gray-800">48h maximum</strong> pour étudier votre projet.
          </p>
          <div className="bg-[#f8fdf9] rounded-2xl border border-[#a8f0c0] p-5 mb-8 text-left">
            <div className="text-xs font-bold uppercase tracking-widest text-[#00a340] mb-3">Récapitulatif de votre demande</div>
            <div className="flex flex-col gap-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Type de travaux</span>
                <span className="font-semibold text-gray-800">{selectedType?.label ?? form.type_travaux}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Surface</span>
                <span className="font-semibold text-gray-800">{form.surface || "—"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Localisation</span>
                <span className="font-semibold text-gray-800">{form.localisation || "—"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Délai souhaité</span>
                <span className="font-semibold text-gray-800">{form.delai || "—"}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              className="px-6 py-3 rounded-lg bg-[#00a340] text-white font-semibold text-sm whitespace-nowrap hover:bg-[#007a30] transition-colors cursor-pointer"
            >
              Retour à l&apos;accueil
            </Link>
            <button
              onClick={() => { setSubmitted(false); setStep(0); setForm(initialData); setDescCount(0); setContraintesCount(0); }}
              className="px-6 py-3 rounded-lg border border-gray-200 text-gray-700 font-semibold text-sm whitespace-nowrap hover:border-[#00a340] hover:text-[#00a340] transition-colors cursor-pointer"
            >
              Nouvelle demande
            </button>
          </div>
        </div>
      </main>
      <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
    <Navbar />
    <main className="pt-20 bg-white">

      {/* Hero compact */}
      <section className="relative h-[220px] md:h-[280px] flex items-end overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=professional%20waterproofing%20expert%20reviewing%20technical%20plans%20and%20blueprints%20on%20construction%20site%2C%20clipboard%20and%20documents%2C%20safety%20helmet%2C%20modern%20building%20background%2C%20professional%20BTP%20photography%2C%20clean%20and%20sharp%2C%20natural%20daylight&width=1440&height=560&seq=ksbtp-devis-hero-001&orientation=landscape"
          alt="Demande de devis KS BTP"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/20"></div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pb-8 md:pb-12">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-green-300 text-xs font-semibold mb-3 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Retour à l&apos;accueil
          </Link>
          <h1
            className="text-2xl md:text-4xl font-extrabold text-white leading-tight mb-1"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Demande de <span className="italic text-[#4cd97a]">devis gratuit</span>
          </h1>
          <p className="text-gray-300 text-sm">Réponse garantie sous 48h — Étude personnalisée et sans engagement</p>
        </div>
      </section>

      {/* Stepper + Form */}
      <section className="py-12 md:py-16 bg-[#f8fdf9]">
        <div className="max-w-3xl mx-auto px-4 md:px-8">

          {/* Stepper */}
          <div className="flex items-center justify-between mb-10 md:mb-14 relative">
            <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200 z-0">
              <div
                className="h-full bg-[#00a340] transition-all duration-500"
                style={{ width: `${(step / (STEPS.length - 1)) * 100}%` }}
              ></div>
            </div>
            {STEPS.map((label, i) => (
              <div key={label} className="relative z-10 flex flex-col items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                    i < step
                      ? "bg-[#00a340] text-white"
                      : i === step
                      ? "bg-[#00a340] text-white ring-4 ring-[#00a340]/20"
                      : "bg-white border-2 border-gray-200 text-gray-400"
                  }`}
                >
                  {i < step ? <i className="ri-check-line text-sm"></i> : i + 1}
                </div>
                <span className={`text-xs font-semibold whitespace-nowrap hidden sm:block ${i === step ? "text-[#00a340]" : "text-gray-400"}`}>
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-10" data-readdy-form>

            {/* STEP 0 — Type de projet */}
            {step === 0 && (
              <div>
                <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Quel type de travaux ?
                </h2>
                <p className="text-gray-500 text-sm mb-7">Sélectionnez le domaine qui correspond à votre projet.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-7">
                  {typesTravaux.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => set("type_travaux", t.value)}
                      className={`flex items-center gap-3 p-4 rounded-xl border text-left transition-all cursor-pointer ${
                        form.type_travaux === t.value
                          ? "border-[#00a340] bg-[#f0fdf4] text-[#00a340]"
                          : "border-gray-200 bg-white text-gray-700 hover:border-[#a8f0c0] hover:bg-[#f8fdf9]"
                      }`}
                    >
                      <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${form.type_travaux === t.value ? "bg-[#00a340] text-white" : "bg-gray-100 text-gray-500"}`}>
                        <i className={`${t.icon} text-base`}></i>
                      </div>
                      <span className="font-semibold text-sm leading-tight">{t.label}</span>
                      {form.type_travaux === t.value && (
                        <i className="ri-check-line ml-auto text-[#00a340]"></i>
                      )}
                    </button>
                  ))}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    Description rapide du projet <span className="text-gray-400 font-normal">(optionnel)</span>
                  </label>
                  <div className="flex justify-end mb-1">
                    <span className={`text-xs ${descCount > 450 ? "text-red-400" : "text-gray-400"}`}>{descCount}/500</span>
                  </div>
                  <textarea
                    name="description_projet"
                    rows={3}
                    maxLength={500}
                    value={form.description_projet}
                    onChange={(e) => { set("description_projet", e.target.value); setDescCount(e.target.value.length); }}
                    placeholder="Décrivez brièvement votre projet d'étanchéité..."
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#00a340] transition-colors resize-none"
                  ></textarea>
                </div>
              </div>
            )}

            {/* STEP 1 — Ouvrage */}
            {step === 1 && (
              <div>
                <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Votre ouvrage
                </h2>
                <p className="text-gray-500 text-sm mb-7">Précisez les caractéristiques de votre chantier.</p>

                <div className="flex flex-col gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Surface approximative *</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {surfaces.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => set("surface", s)}
                          className={`px-3 py-2.5 rounded-lg border text-xs font-semibold text-center transition-all cursor-pointer ${
                            form.surface === s
                              ? "border-[#00a340] bg-[#f0fdf4] text-[#00a340]"
                              : "border-gray-200 bg-white text-gray-600 hover:border-[#a8f0c0]"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Type de bâtiment</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {typesBatiment.map((b) => (
                        <button
                          key={b}
                          type="button"
                          onClick={() => set("type_batiment", b)}
                          className={`px-3 py-2.5 rounded-lg border text-xs font-semibold text-left transition-all cursor-pointer ${
                            form.type_batiment === b
                              ? "border-[#00a340] bg-[#f0fdf4] text-[#00a340]"
                              : "border-gray-200 bg-white text-gray-600 hover:border-[#a8f0c0]"
                          }`}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Localisation du chantier *</label>
                    <input
                      type="text"
                      name="localisation"
                      value={form.localisation}
                      onChange={(e) => set("localisation", e.target.value)}
                      placeholder="Ville, département ou adresse"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#00a340] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Délai souhaité</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {delais.map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => set("delai", d)}
                          className={`px-3 py-2.5 rounded-lg border text-xs font-semibold text-left transition-all cursor-pointer ${
                            form.delai === d
                              ? "border-[#00a340] bg-[#f0fdf4] text-[#00a340]"
                              : "border-gray-200 bg-white text-gray-600 hover:border-[#a8f0c0]"
                          }`}
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Support existant</label>
                    <input
                      type="text"
                      name="support_existant"
                      value={form.support_existant}
                      onChange={(e) => set("support_existant", e.target.value)}
                      placeholder="Ex : béton, bois, métal, étanchéité existante à rénover..."
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#00a340] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Contraintes particulières <span className="text-gray-400 font-normal">(optionnel)</span>
                    </label>
                    <div className="flex justify-end mb-1">
                      <span className={`text-xs ${contraintesCount > 450 ? "text-red-400" : "text-gray-400"}`}>{contraintesCount}/500</span>
                    </div>
                    <textarea
                      name="contraintes"
                      rows={3}
                      maxLength={500}
                      value={form.contraintes}
                      onChange={(e) => { set("contraintes", e.target.value); setContraintesCount(e.target.value.length); }}
                      placeholder="Accès difficile, site occupé, contraintes techniques, normes spécifiques..."
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#00a340] transition-colors resize-none"
                    ></textarea>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 2 — Coordonnées */}
            {step === 2 && (
              <div>
                <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Vos coordonnées
                </h2>
                <p className="text-gray-500 text-sm mb-7">Pour que nous puissions vous recontacter avec votre devis.</p>

                <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Prénom *</label>
                      <input
                        type="text"
                        name="prenom"
                        value={form.prenom}
                        onChange={(e) => set("prenom", e.target.value)}
                        required
                        placeholder="Jean"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#00a340] transition-colors"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nom *</label>
                      <input
                        type="text"
                        name="nom"
                        value={form.nom}
                        onChange={(e) => set("nom", e.target.value)}
                        required
                        placeholder="Dupont"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#00a340] transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      required
                      placeholder="jean.dupont@email.fr"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#00a340] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Téléphone *</label>
                    <input
                      type="tel"
                      name="telephone"
                      value={form.telephone}
                      onChange={(e) => set("telephone", e.target.value)}
                      required
                      placeholder="+33 6 XX XX XX XX"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#00a340] transition-colors"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Société / Organisation</label>
                      <input
                        type="text"
                        name="societe"
                        value={form.societe}
                        onChange={(e) => set("societe", e.target.value)}
                        placeholder="Nom de votre entreprise"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#00a340] transition-colors"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Poste / Fonction</label>
                      <input
                        type="text"
                        name="poste"
                        value={form.poste}
                        onChange={(e) => set("poste", e.target.value)}
                        placeholder="Ex : Responsable technique"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#00a340] transition-colors"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3 — Récapitulatif */}
            {step === 3 && (
              <div>
                <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Récapitulatif
                </h2>
                <p className="text-gray-500 text-sm mb-7">Vérifiez vos informations avant d&apos;envoyer votre demande.</p>

                <div className="flex flex-col gap-4">
                  {/* Projet */}
                  <div className="rounded-xl border border-gray-100 bg-[#f8fdf9] p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold uppercase tracking-widest text-[#00a340]">Votre projet</span>
                      <button type="button" onClick={() => setStep(0)} className="text-xs text-gray-400 hover:text-[#00a340] cursor-pointer transition-colors">
                        Modifier
                      </button>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Type de travaux</span>
                        <span className="font-semibold text-gray-800">{selectedType?.label ?? "—"}</span>
                      </div>
                      {form.description_projet && (
                        <div className="flex justify-between text-sm gap-4">
                          <span className="text-gray-500 flex-shrink-0">Description</span>
                          <span className="font-semibold text-gray-800 text-right">{form.description_projet}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Ouvrage */}
                  <div className="rounded-xl border border-gray-100 bg-[#f8fdf9] p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold uppercase tracking-widest text-[#00a340]">Votre ouvrage</span>
                      <button type="button" onClick={() => setStep(1)} className="text-xs text-gray-400 hover:text-[#00a340] cursor-pointer transition-colors">
                        Modifier
                      </button>
                    </div>
                    <div className="flex flex-col gap-2">
                      {[
                        { label: "Surface", value: form.surface },
                        { label: "Type de bâtiment", value: form.type_batiment },
                        { label: "Localisation", value: form.localisation },
                        { label: "Délai souhaité", value: form.delai },
                        { label: "Support existant", value: form.support_existant },
                        { label: "Contraintes", value: form.contraintes },
                      ].filter((r) => r.value).map((row) => (
                        <div key={row.label} className="flex justify-between text-sm gap-4">
                          <span className="text-gray-500 flex-shrink-0">{row.label}</span>
                          <span className="font-semibold text-gray-800 text-right">{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Coordonnées */}
                  <div className="rounded-xl border border-gray-100 bg-[#f8fdf9] p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold uppercase tracking-widest text-[#00a340]">Vos coordonnées</span>
                      <button type="button" onClick={() => setStep(2)} className="text-xs text-gray-400 hover:text-[#00a340] cursor-pointer transition-colors">
                        Modifier
                      </button>
                    </div>
                    <div className="flex flex-col gap-2">
                      {[
                        { label: "Nom", value: `${form.prenom} ${form.nom}` },
                        { label: "Email", value: form.email },
                        { label: "Téléphone", value: form.telephone },
                        { label: "Société", value: form.societe },
                        { label: "Poste", value: form.poste },
                      ].filter((r) => r.value.trim()).map((row) => (
                        <div key={row.label} className="flex justify-between text-sm gap-4">
                          <span className="text-gray-500 flex-shrink-0">{row.label}</span>
                          <span className="font-semibold text-gray-800 text-right">{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <p className="text-xs text-gray-400 text-center leading-relaxed">
                    En soumettant ce formulaire, vous acceptez d&apos;être recontacté par KS BTP concernant votre projet d&apos;étanchéité.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
              {step > 0 ? (
                <button
                  type="button"
                  onClick={() => setStep((s) => s - 1)}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-gray-200 text-gray-600 text-sm font-semibold hover:border-gray-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Précédent
                </button>
              ) : (
                <Link
                  to="/"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-gray-200 text-gray-600 text-sm font-semibold hover:border-gray-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Retour
                </Link>
              )}

              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!canNext()}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#00a340] text-white text-sm font-bold hover:bg-[#007a30] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Suivant
                  <i className="ri-arrow-right-line"></i>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#00a340] text-white text-sm font-bold hover:bg-[#007a30] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Envoi...</>
                  ) : (
                    <><i className="ri-send-plane-line"></i> Envoyer ma demande</>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Garanties sous le form */}
          <div className="mt-6 grid grid-cols-3 gap-3">
            {[
              { icon: "ri-shield-check-line", text: "Devis 100% gratuit" },
              { icon: "ri-time-line", text: "Réponse sous 48h" },
              { icon: "ri-lock-line", text: "Sans engagement" },
            ].map((g) => (
              <div key={g.text} className="flex items-center gap-2 justify-center bg-white rounded-xl border border-gray-100 px-3 py-3">
                <div className="w-6 h-6 flex items-center justify-center text-[#00a340]">
                  <i className={`${g.icon} text-base`}></i>
                </div>
                <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">{g.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

    </main>
    <Footer />
    </div>
  );
}
